import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import type { MouseEvent, ReactNode } from "react";

interface RoundedPaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
}

function RoundedPaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: RoundedPaginationActionsProps) {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5, display: "inline-flex", gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={(e) => onPageChange(e, page - 1)}
        disabled={page === 0}
        aria-label="previous page"
      >
        <RavenIcon icon={ChevronLeft} fontSize="medium" />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= lastPage}
        aria-label="next page"
      >
        <RavenIcon icon={ChevronRight} fontSize="medium" />
      </IconButton>
    </Box>
  );
}

/**
 * Still ESS's own implementation rather than the package's `DataTable`, which
 * this type deliberately mirrors (`field`/`header` here, `key`/`label`
 * there). The package version keeps its border, radius, header treatment,
 * row height and empty state in `DataTable.css`, which ESS does not load
 * (CLAUDE.md §6); on top of that it has no sticky header, appends an extra
 * chevron column whenever `onRowClick` is set, paginates with MUI's stock
 * actions over a [5, 10, 25, 50] ladder, and does not fill its parent
 * vertically. Adopting it today would cost every one of those. `ds/Table`'s
 * props are kept name-compatible so the swap stays a rename.
 */
export interface DataTableColumn<T> {
  field: keyof T | string;
  header: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  render?: (row: T) => ReactNode;
  tooltip?: boolean;
}

export interface TableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: keyof T;
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rpp: number) => void;
  onRowClick?: (row: T) => void;
  isRowHighlighted?: (row: T) => boolean;
  getRowTone?: (row: T) => "error" | "warning" | undefined;
  loading?: boolean;
  emptyLabel?: string;
  /** Suppress the outer Paper border/radius — use when the Table is already inside a Card. */
  noBorder?: boolean;
}

export default function Table<T>({
  columns,
  rows,
  rowKey,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  isRowHighlighted,
  getRowTone,
  loading,
  emptyLabel = "No records",
  noBorder = false,
}: TableProps<T>) {
  const theme = useTheme();
  const rowStyles = (row: T) => {
    const tone = getRowTone?.(row);
    const color = tone ? theme.palette[tone].main : theme.palette.primary.main;
    return {
      backgroundColor: tone
        ? alpha(color, 0.08)
        : isRowHighlighted?.(row)
          ? alpha(theme.palette.success.main, 0.08)
          : undefined,
      cursor: onRowClick ? "pointer" : "default",
      "&:last-of-type td": { borderBottom: 0 },
      "&:hover": {
        backgroundColor: `${alpha(color, tone ? 0.12 : 0.04)} !important`,
      },
      "& td": { height: 48 },
    };
  };

  const rowsPerPageOptions = [10, 25, 50].includes(rowsPerPage)
    ? [10, 25, 50]
    : [...[10, 25, 50], rowsPerPage].sort((a, b) => a - b);

  // With `table-layout: fixed`, a column left without a width becomes the
  // "leftover" column — and when the fixed columns nearly fill the
  // container it collapses to ~0 (the Title column did exactly this).
  // Floor the table at the sum of the explicit column widths so those
  // columns keep their size and the container scrolls horizontally instead
  // of crushing the flexible one. Auto-width columns contribute 0, so a
  // mobile layout whose primary column is flexible never gets forced to
  // scroll.
  const minTableWidth = columns.reduce(
    (sum, c) => sum + (typeof c.width === "number" ? c.width : 0),
    0,
  );

  return (
    <Paper
      variant={noBorder ? "elevation" : "outlined"}
      elevation={0}
      sx={{
        borderRadius: noBorder ? 0 : "24px",
        overflow: "hidden",
        borderColor: theme.palette.divider,
        ...(noBorder && { border: "none" }),
        // Fill the parent card vertically so the pagination footer sits
        // at the card's bottom edge regardless of how many rows render.
        // Parent passes flex:1 → this paper grows; rows scroll inside.
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <MuiTable
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: minTableWidth || undefined,
          }}
        >
          <TableHead>
            <TableRow sx={{ height: 40 }}>
              {columns.map((c, idx) => (
                <TableCell
                  key={String(c.field)}
                  align={c.align ?? "left"}
                  sx={{
                    width: c.width,
                    ...theme.typography.tableHeader,
                    background: theme.palette.background.default,
                    color: "text.primary",
                    borderColor: theme.palette.divider,
                    ...(idx === 0 && { borderTopLeftRadius: 16 }),
                    ...(idx === columns.length - 1 && {
                      borderTopRightRadius: 16,
                    }),
                  }}
                >
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">{emptyLabel}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  hover
                  key={String(row[rowKey])}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={rowStyles(row)}
                >
                  {columns.map((c) => {
                    const raw = (row as Record<string, unknown>)[
                      String(c.field)
                    ];
                    const content = c.render
                      ? c.render(row)
                      : ((raw as ReactNode) ?? "—");

                    return (
                      <TableCell
                        key={String(c.field)}
                        align={c.align ?? "left"}
                        sx={{
                          width: c.width,
                          borderColor: theme.palette.divider,
                          "&:focus": { outline: "none" },
                          "&:focus-within": { outline: "none" },
                        }}
                      >
                        {c.tooltip ? (
                          <Tooltip
                            title={String(raw ?? "")}
                            placement="top-start"
                          >
                            <Box
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {content}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {content}
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          borderTop: "1px solid",
          borderColor: "divider",
          // Pin to the card's bottom edge — never collapse even when the
          // rows above don't fill the available space.
          flexShrink: 0,
        }}
      >
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={rowsPerPageOptions}
          ActionsComponent={RoundedPaginationActions}
        />
      </Box>
    </Paper>
  );
}
